import React, { useState, useEffect } from 'react';
import { updateTemps, getConsultants, getProjets, getClients, getPrestataires, recalculerTMPTrimestre } from '../services/airtable';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../styles/colors';
import { formatEuro } from '../utils/formatters';
import { MOIS } from '../utils/constants';
import { Calendar, Filter, Pencil, Trash2, X, AlertTriangle } from 'lucide-react';
import AnimatedContent from './AnimatedContent';
import DeleteConfirmModal from './modals/DeleteConfirmModal';
import { useTempsList } from '../hooks/useTempsList';

/**
 * TempsList - Liste des temps avec filtrage par année
 *
 * Refactorisé pour utiliser:
 * - useTempsList: hook de gestion d'état
 * - DeleteConfirmModal: modale de confirmation réutilisable
 * - MOIS: constantes centralisées
 */
function TempsList({ refresh }) {
  const { user, isSuperAdmin, canUserEditTemps, canUserDeleteTemps } = useAuth();

  const {
    loading,
    error,
    selectedYear,
    availableYears,
    setSelectedYear,
    editingRecord,
    showEditModal,
    handleEdit,
    handleEditClose,
    handleEditSuccess,
    deletingRecord,
    showDeleteModal,
    deleting,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    getFilteredAndGroupedTemps,
    getMonthTotals
  } = useTempsList(refresh);

  const { isEmpty, tempsByMonth, sortedMonths } = getFilteredAndGroupedTemps();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Filtre année */}
      <YearFilter
        selectedYear={selectedYear}
        availableYears={availableYears}
        onYearChange={setSelectedYear}
      />

      {/* Message d'erreur */}
      {error && <ErrorMessage error={error} />}

      {/* Contenu principal */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <AnimatedContent>
          {isEmpty ? (
            <EmptyState selectedYear={selectedYear} />
          ) : (
            <div style={{ padding: '0 2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {sortedMonths.map(mois => {
                const tempsOfMonth = tempsByMonth[mois];
                const { totalJours, totalCA } = getMonthTotals(tempsOfMonth);
                const monthLabel = MOIS.find(m => m.value === parseInt(mois))?.label || mois;

                return (
                  <MonthTable
                    key={mois}
                    month={monthLabel}
                    year={selectedYear}
                    temps={tempsOfMonth}
                    totalJours={totalJours}
                    totalCA={totalCA}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    canUserEditTemps={canUserEditTemps}
                    canUserDeleteTemps={canUserDeleteTemps}
                  />
                );
              })}
            </div>
          )}
        </AnimatedContent>
      )}

      {/* Modale d'édition */}
      {showEditModal && editingRecord && (
        <EditTempsModal
          record={editingRecord}
          onClose={handleEditClose}
          onSuccess={handleEditSuccess}
          user={user}
          isSuperAdmin={isSuperAdmin}
        />
      )}

      {/* Modale de suppression */}
      <DeleteConfirmModal
        isOpen={showDeleteModal && !!deletingRecord}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        loading={deleting}
      >
        <p style={{ margin: 0 }}>
          Voulez-vous vraiment supprimer ce temps ?<br />
          <strong>{deletingRecord?.consultantNom}</strong> - {deletingRecord?.projetNom}<br />
          <span style={{ color: colors.secondary.mediumGray }}>
            {deletingRecord?.fields?.['Nombre de jours']} jours
          </span>
        </p>
      </DeleteConfirmModal>
    </div>
  );
}

// === SOUS-COMPOSANTS ===

function YearFilter({ selectedYear, availableYears, onYearChange }) {
  return (
    <div style={yearFilterStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Filter size={20} color={colors.primary.teal} />
        <span style={filterLabelStyle}>Filtrer par année :</span>
      </div>
      <select
        value={selectedYear}
        onChange={(e) => onYearChange(parseInt(e.target.value))}
        style={yearSelectStyle}
        onFocus={(e) => e.target.style.borderColor = colors.primary.teal}
        onBlur={(e) => e.target.style.borderColor = colors.secondary.lightTeal}
      >
        {availableYears.map(year => (
          <option key={year} value={year}>{year}</option>
        ))}
      </select>
    </div>
  );
}

function ErrorMessage({ error }) {
  return (
    <div style={errorStyle}>
      Erreur : {error}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div style={loadingContainerStyle}>
      <div style={spinnerStyle} />
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function EmptyState({ selectedYear }) {
  return (
    <div style={emptyStateStyle}>
      <Calendar size={48} color={colors.secondary.mediumGray} style={{ margin: '0 auto 1rem' }} />
      <h3 style={emptyTitleStyle}>
        Aucun temps saisi pour {selectedYear}
      </h3>
      <p style={emptySubtitleStyle}>
        Commencez par saisir vos premiers temps
      </p>
    </div>
  );
}

function MonthTable({ month, year, temps, totalJours, totalCA, onEdit, onDelete, canUserEditTemps, canUserDeleteTemps }) {
  return (
    <div>
      {/* Header du mois */}
      <div style={monthHeaderStyle}>
        <h3 style={monthTitleStyle}>{month} {year}</h3>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={totalLabelStyle}>Total jours :</span>
            <span style={totalValueStyle}>{totalJours.toFixed(1)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={totalLabelStyle}>CA total :</span>
            <span style={totalCAStyle}>{formatEuro(totalCA)}</span>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div style={tableContainerStyle}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ backgroundColor: colors.primary.gray }}>
                <th style={{ ...headerCellStyle, width: '14%' }}>Consultant</th>
                <th style={{ ...headerCellStyle, width: '13%' }}>Prestataire</th>
                <th style={{ ...headerCellStyle, width: '13%' }}>Client final</th>
                <th style={{ ...headerCellStyle, width: '16%' }}>Mission</th>
                <th style={{ ...headerCellStyleCenter, width: '9%' }}>Taux</th>
                <th style={{ ...headerCellStyleCenter, width: '7%' }}>Jours</th>
                <th style={{ ...headerCellStyleCenter, width: '9%' }}>CA</th>
                <th style={{ ...headerCellStyleCenter, width: '5%' }} title="Inclus dans le calcul TMP">TMP</th>
                <th style={{ ...headerCellStyleCenter, width: '14%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {temps.map((record, index) => (
                <TempsRow
                  key={record.id}
                  record={record}
                  index={index}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  canUserEditTemps={canUserEditTemps}
                  canUserDeleteTemps={canUserDeleteTemps}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TempsRow({ record, index, onEdit, onDelete, canUserEditTemps, canUserDeleteTemps }) {
  const canEdit = canUserEditTemps(record);
  const canDelete = canUserDeleteTemps(record);

  return (
    <tr
      style={{ backgroundColor: index % 2 === 0 ? colors.text.white : colors.secondary.lightGray, transition: 'background-color 200ms' }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.secondary.lightTeal}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? colors.text.white : colors.secondary.lightGray}
    >
      <td style={cellStyle}>{record.consultantNom || '-'}</td>
      <td style={cellStyle}>{record.prestataireNom || record.fields['Prestataire'] || '-'}</td>
      <td style={cellStyle}>{record.clientNom || record.fields['Client final'] || '-'}</td>
      <td style={cellStyle}>{record.projetNom || '-'}</td>
      <td style={cellStyleCenter}>{formatEuro(record.fields['Taux journalier (€)'])}</td>
      <td style={{ ...cellStyleCenter, fontWeight: '500' }}>{record.fields['Nombre de jours']}</td>
      <td style={{ ...cellStyleCenter, fontWeight: '600', color: colors.primary.teal }}>{formatEuro(record.fields['CA calculé'])}</td>
      <td style={{ ...cellStyleCenter, fontSize: '1rem' }} title={record.fields?.tmpexcl ? 'Exclu du calcul TMP' : 'Inclus dans le calcul TMP'}>
        {record.fields?.tmpexcl ? (
          <span style={{ color: colors.status.warning || '#f59e0b' }}>✗</span>
        ) : (
          <span style={{ color: colors.status.success || '#22c55e' }}>✓</span>
        )}
      </td>
      <td style={cellStyleCenter}>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
          <ActionButton
            onClick={() => canEdit && onEdit(record)}
            disabled={!canEdit}
            icon={<Pencil size={16} color={colors.text.white} />}
            title={canEdit ? "Modifier" : "Vous ne pouvez modifier que vos propres temps"}
            color={colors.primary.gray}
          />
          <ActionButton
            onClick={() => canDelete && onDelete(record)}
            disabled={!canDelete}
            icon={<Trash2 size={16} color={canDelete ? "#dc2626" : colors.secondary.mediumGray} />}
            title={canDelete ? "Supprimer" : "Vous ne pouvez supprimer que vos propres temps"}
            color="#fee2e2"
            disabledColor={colors.secondary.lightGray}
          />
        </div>
      </td>
    </tr>
  );
}

function ActionButton({ onClick, disabled, icon, title, color, disabledColor }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '0.375rem',
        borderRadius: '0.375rem',
        border: 'none',
        backgroundColor: disabled ? (disabledColor || colors.secondary.mediumGray) : color,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? 0.5 : 1
      }}
      title={title}
    >
      {icon}
    </button>
  );
}

function EditTempsModal({ record, onClose, onSuccess, user, isSuperAdmin }) {
  const [formData, setFormData] = useState({
    'Année': record.fields.Année || new Date().getFullYear(),
    'Mois': record.fields.Mois || 1,
    'Consultant': record.fields.Consultant || [],
    'Prestataire': [],
    'Client': record.fields['Client final'] || [],
    'Projet': record.fields.Projet || [],
    'Nombre de jours': record.fields['Nombre de jours'] || '',
    'Taux journalier (€)': record.fields['Taux journalier (€)'] || '',
    'Notes': record.fields.Notes || '',
    'tmpexcl': record.fields.tmpexcl || false
  });
  const [consultants, setConsultants] = useState([]);
  const [prestataires, setPrestataires] = useState([]);
  const [projets, setProjets] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientsFiltres, setClientsFiltres] = useState([]);
  const [projetsFiltres, setProjetsFiltres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [consultantsData, projetsData, clientsData, prestatairesData] = await Promise.all([
          getConsultants(), getProjets(), getClients(), getPrestataires()
        ]);
        setConsultants(consultantsData);
        setProjets(projetsData);
        const prestatairesList = prestatairesData.map(p => ({
          id: p.id,
          nom: p.fields.Nom || p.fields['Nom du prestataire'] || 'Prestataire inconnu'
        }));
        setPrestataires(prestatairesList);
        const clientsList = clientsData.map(c => ({
          id: c.id,
          nom: c.fields.Nom || c.fields['Nom du client'] || 'Client inconnu',
          prestataireId: c.fields['Prestataire porteur']?.[0] || null
        }));
        setClients(clientsList);

        // Initialiser les filtres avec les valeurs existantes
        const projetId = record.fields.Projet?.[0];
        const clientId = record.fields['Client final']?.[0];
        if (projetId) {
          const projet = projetsData.find(p => p.id === projetId);
          if (projet) {
            const projetClientId = clientId || projet.fields['Clients finaux']?.[0];
            if (projetClientId) {
              const client = clientsList.find(c => c.id === projetClientId);
              if (client && client.prestataireId) {
                setFormData(prev => ({ ...prev, 'Prestataire': [client.prestataireId] }));
                const filteredClients = clientsList.filter(c => c.prestataireId === client.prestataireId);
                setClientsFiltres(filteredClients);
                setFormData(prev => ({ ...prev, 'Client': [projetClientId] }));
                const filteredProjets = projetsData.filter(p => p.fields['Clients finaux']?.includes(projetClientId));
                setProjetsFiltres(filteredProjets);
              }
            }
          }
        }
      } catch (err) {
        setError('Erreur lors du chargement des données');
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [record]);

  const filterClientsByPrestataire = (prestataireId) => {
    if (!prestataireId) {
      setClientsFiltres([]);
      return;
    }
    setClientsFiltres(clients.filter(c => c.prestataireId === prestataireId));
  };

  const filterProjetsByClient = (clientId) => {
    if (!clientId) {
      setProjetsFiltres([]);
      return;
    }
    setProjetsFiltres(projets.filter(p => p.fields['Clients finaux']?.includes(clientId)));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }
    if (name === 'Prestataire') {
      setFormData(prev => ({ ...prev, [name]: value ? [value] : [], 'Client': [], 'Projet': [] }));
      filterClientsByPrestataire(value);
      setProjetsFiltres([]);
    } else if (name === 'Client') {
      setFormData(prev => ({ ...prev, [name]: value ? [value] : [], 'Projet': [] }));
      filterProjetsByClient(value);
    } else if (name === 'Consultant' || name === 'Projet') {
      setFormData(prev => ({ ...prev, [name]: value ? [value] : [] }));
    } else if (['Nombre de jours', 'Taux journalier (€)', 'Année', 'Mois'].includes(name)) {
      setFormData(prev => ({ ...prev, [name]: value ? parseFloat(value) : '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const dataToSend = {
        'Année': parseInt(formData['Année'], 10),
        'Mois': parseInt(formData['Mois'], 10),
        'Nombre de jours': parseFloat(formData['Nombre de jours']),
        'Taux journalier (€)': parseFloat(formData['Taux journalier (€)'])
      };
      if (formData['Consultant']?.[0]) dataToSend['Consultant'] = formData['Consultant'];
      if (formData['Projet']?.[0]) dataToSend['Projet'] = formData['Projet'];
      if (formData['Notes']?.trim()) dataToSend['Notes'] = formData['Notes'];
      dataToSend['tmpexcl'] = formData['tmpexcl'] || false;
      await updateTemps(record.id, dataToSend, formData['Client']?.[0]);

      // Recalculer le TMP du trimestre
      const mois = parseInt(formData['Mois'], 10);
      const annee = parseInt(formData['Année'], 10);
      const trimestre = mois >= 1 && mois <= 3 ? 'Q1' :
                       mois >= 4 && mois <= 6 ? 'Q2' :
                       mois >= 7 && mois <= 9 ? 'Q3' : 'Q4';

      try {
        await recalculerTMPTrimestre(annee, trimestre);
        console.log(`✅ TMP recalculé pour ${annee}-${trimestre}`);
      } catch (tmpError) {
        console.error('❌ Erreur lors du recalcul du TMP:', tmpError);
      }

      onSuccess();
    } catch (err) {
      setError('Erreur lors de la modification: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const annees = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4, currentYear - 5];

  return (
    <div style={editModalOverlayStyle}>
      <div style={editModalStyle}>
        {/* Header */}
        <div style={editModalHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Pencil size={24} color={colors.text.white} />
            <h2 style={editModalTitleStyle}>Modifier le temps</h2>
          </div>
          <button onClick={onClose} style={closeButtonStyle}>
            <X size={24} color={colors.text.white} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {loadingData ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={spinnerStyle} />
              <p style={{ marginTop: '1rem', color: colors.secondary.mediumGray }}>Chargement...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {/* Erreur */}
              {error && (
                <div style={formErrorStyle}>
                  <AlertTriangle size={20} color="#dc2626" />
                  <span style={{ color: '#991b1b', fontSize: '0.875rem' }}>{error}</span>
                </div>
              )}

              {/* Année / Mois / Consultant */}
              <div style={gridStyle}>
                <FormField label="Année" required>
                  <select name="Année" value={formData['Année']} onChange={handleChange} required style={inputStyle}>
                    {annees.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </FormField>
                <FormField label="Mois" required>
                  <select name="Mois" value={formData['Mois']} onChange={handleChange} required style={inputStyle}>
                    {MOIS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </FormField>
                <FormField label="Consultant" required>
                  <select
                    name="Consultant"
                    value={formData['Consultant'][0] || ''}
                    onChange={handleChange}
                    required
                    disabled={!isSuperAdmin()}
                    style={{
                      ...inputStyle,
                      backgroundColor: !isSuperAdmin() ? colors.secondary.lightGray : colors.text.white,
                      cursor: !isSuperAdmin() ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">-- Sélectionner --</option>
                    {consultants.filter(c => isSuperAdmin() || c.id === user?.consultant_id).map(c => (
                      <option key={c.id} value={c.id}>{c.fields.Nom}</option>
                    ))}
                  </select>
                </FormField>
              </div>

              {/* Prestataire / Client / Projet */}
              <div style={gridStyle}>
                <FormField label="Prestataire" required>
                  <select name="Prestataire" value={formData['Prestataire'][0] || ''} onChange={handleChange} required style={inputStyle}>
                    <option value="">-- Sélectionner --</option>
                    {prestataires.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                  </select>
                </FormField>
                <FormField label="Client" required disabled={!formData['Prestataire'][0]}>
                  <select
                    name="Client"
                    value={formData['Client'][0] || ''}
                    onChange={handleChange}
                    required
                    disabled={!formData['Prestataire'][0]}
                    style={{
                      ...inputStyle,
                      backgroundColor: !formData['Prestataire'][0] ? colors.secondary.lightGray : colors.text.white,
                      cursor: !formData['Prestataire'][0] ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">-- Sélectionner --</option>
                    {clientsFiltres.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                </FormField>
                <FormField label="Projet" required disabled={!formData['Client'][0]}>
                  <select
                    name="Projet"
                    value={formData['Projet'][0] || ''}
                    onChange={handleChange}
                    required
                    disabled={!formData['Client'][0]}
                    style={{
                      ...inputStyle,
                      backgroundColor: !formData['Client'][0] ? colors.secondary.lightGray : colors.text.white,
                      cursor: !formData['Client'][0] ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">-- Sélectionner --</option>
                    {projetsFiltres.map(p => <option key={p.id} value={p.id}>{p.fields['Nom du projet'] || p.fields.Nom}</option>)}
                  </select>
                </FormField>
              </div>

              {/* Jours / Taux */}
              <div style={gridStyle}>
                <FormField label="Nombre de jours" required>
                  <input type="number" name="Nombre de jours" value={formData['Nombre de jours']} onChange={handleChange} step="0.5" min="0" required style={inputStyle} />
                </FormField>
                <FormField label="Taux journalier (€)" required>
                  <input type="number" name="Taux journalier (€)" value={formData['Taux journalier (€)']} onChange={handleChange} step="0.01" min="0" required style={inputStyle} />
                </FormField>
                <div></div>
              </div>

              {/* Notes */}
              <FormField label="Notes">
                <textarea name="Notes" value={formData['Notes']} onChange={handleChange} rows="3" style={{ ...inputStyle, resize: 'vertical' }} placeholder="Notes optionnelles..." />
              </FormField>

              {/* Checkbox TMP */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0' }}>
                <input
                  type="checkbox"
                  id="tmpexcl-edit"
                  name="tmpexcl"
                  checked={formData['tmpexcl']}
                  onChange={handleChange}
                  style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: colors.primary.teal }}
                />
                <label htmlFor="tmpexcl-edit" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
                  Exclure du calcul TMP
                </label>
              </div>

              {/* Boutons */}
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button type="button" onClick={onClose} style={cancelFormButtonStyle}>Annuler</button>
                <button type="submit" disabled={loading} style={{ ...submitButtonStyle, backgroundColor: loading ? colors.secondary.mediumGray : colors.primary.teal, cursor: loading ? 'not-allowed' : 'pointer' }}>
                  {loading ? '⏳ Enregistrement...' : '✓ Enregistrer les modifications'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function FormField({ label, required, disabled, children }) {
  return (
    <div>
      <label style={{ ...labelStyle, color: disabled ? colors.secondary.mediumGray : colors.primary.gray }}>
        {label} {required && <span style={{ color: disabled ? colors.secondary.mediumGray : colors.primary.teal }}>*</span>}
      </label>
      {children}
    </div>
  );
}

// === STYLES ===

const yearFilterStyle = {
  backgroundColor: colors.text.white,
  borderRadius: '0.75rem',
  padding: '1rem 1.5rem',
  border: `1px solid ${colors.secondary.lightTeal}`,
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  flexWrap: 'wrap'
};

const filterLabelStyle = {
  fontFamily: '"Noto Sans", sans-serif',
  fontSize: '0.9375rem',
  fontWeight: '500',
  color: colors.primary.gray
};

const yearSelectStyle = {
  padding: '0.5rem 2.5rem 0.5rem 0.75rem',
  border: `2px solid ${colors.secondary.lightTeal}`,
  borderRadius: '0.5rem',
  fontSize: '0.9375rem',
  fontFamily: '"Noto Sans", sans-serif',
  color: colors.primary.gray,
  backgroundColor: colors.text.white,
  cursor: 'pointer',
  outline: 'none',
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23007481' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.75rem center'
};

const errorStyle = {
  backgroundColor: colors.background.lightRed,
  border: `2px solid ${colors.status.error}`,
  color: colors.text.darkRed,
  padding: '1rem',
  borderRadius: '0.5rem',
  fontFamily: '"Noto Sans", sans-serif'
};

const loadingContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px',
  fontFamily: '"Noto Sans", sans-serif',
  gap: '1rem'
};

const spinnerStyle = {
  width: '48px',
  height: '48px',
  border: `4px solid ${colors.secondary.lightTeal}`,
  borderTop: `4px solid ${colors.primary.teal}`,
  borderRadius: '50%',
  animation: 'spin 1s linear infinite'
};

const emptyStateStyle = {
  backgroundColor: colors.text.white,
  borderRadius: '1rem',
  padding: '3rem',
  textAlign: 'center',
  border: `1px solid ${colors.secondary.lightTeal}`,
  marginTop: '1.5rem'
};

const emptyTitleStyle = {
  margin: 0,
  fontSize: '1.25rem',
  color: colors.primary.gray,
  fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif'
};

const emptySubtitleStyle = {
  margin: '0.5rem 0 0 0',
  color: colors.secondary.mediumGray,
  fontFamily: '"Noto Sans", sans-serif'
};

const monthHeaderStyle = {
  backgroundColor: colors.text.white,
  padding: '1rem 0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '1rem'
};

const monthTitleStyle = {
  margin: 0,
  fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif',
  fontSize: '1.25rem',
  color: colors.primary.teal,
  fontWeight: 'bold'
};

const totalLabelStyle = {
  fontSize: '0.875rem',
  color: colors.primary.gray,
  fontFamily: '"Noto Sans", sans-serif'
};

const totalValueStyle = {
  fontSize: '1.125rem',
  fontWeight: 'bold',
  color: colors.primary.teal,
  fontFamily: '"Noto Sans", sans-serif'
};

const totalCAStyle = {
  fontSize: '1.125rem',
  fontWeight: 'bold',
  color: colors.primary.gold,
  fontFamily: '"Noto Sans", sans-serif'
};

const tableContainerStyle = {
  backgroundColor: colors.text.white,
  borderRadius: '1rem',
  overflow: 'hidden',
  border: `1px solid ${colors.primary.gray}`,
  boxShadow: '0 2px 8px rgba(0, 116, 129, 0.1)'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: '"Noto Sans", sans-serif',
  tableLayout: 'fixed'
};

const headerCellStyle = {
  padding: '0.625rem 0.75rem',
  textAlign: 'left',
  fontSize: '0.8125rem',
  fontWeight: '600',
  color: colors.text.white,
  textTransform: 'uppercase',
  letterSpacing: '0.025em',
  borderBottom: `2px solid ${colors.secondary.mediumGray}`
};

const headerCellStyleCenter = {
  ...headerCellStyle,
  textAlign: 'center'
};

const cellStyle = {
  padding: '0.5rem 0.75rem',
  fontSize: '0.8rem',
  color: colors.primary.gray,
  borderBottom: `1px solid ${colors.secondary.mediumGray}`,
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

const cellStyleCenter = {
  ...cellStyle,
  textAlign: 'center'
};

// Edit Modal styles
const editModalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '2rem'
};

const editModalStyle = {
  backgroundColor: colors.text.white,
  borderRadius: '1rem',
  width: '100%',
  maxWidth: '900px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
};

const editModalHeaderStyle = {
  padding: '1.25rem 1.5rem',
  backgroundColor: colors.primary.teal,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
};

const editModalTitleStyle = {
  margin: 0,
  fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif',
  fontSize: '1.25rem',
  color: colors.text.white,
  fontWeight: 'bold'
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '0.25rem'
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '1rem'
};

const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  border: `2px solid ${colors.secondary.lightTeal}`,
  borderRadius: '0.5rem',
  fontSize: '0.9375rem',
  fontFamily: '"Noto Sans", sans-serif',
  color: colors.primary.gray,
  backgroundColor: colors.text.white,
  outline: 'none',
  boxSizing: 'border-box'
};

const labelStyle = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: '500',
  color: colors.primary.gray,
  marginBottom: '0.5rem',
  fontFamily: '"Noto Sans", sans-serif'
};

const formErrorStyle = {
  padding: '1rem',
  borderRadius: '0.5rem',
  backgroundColor: '#fee2e2',
  border: '1px solid #ef4444',
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem'
};

const cancelFormButtonStyle = {
  padding: '0.75rem 1.5rem',
  borderRadius: '0.5rem',
  border: `2px solid ${colors.secondary.mediumGray}`,
  backgroundColor: 'transparent',
  color: colors.primary.gray,
  fontSize: '1rem',
  fontWeight: '600',
  fontFamily: '"Noto Sans", sans-serif',
  cursor: 'pointer'
};

const submitButtonStyle = {
  padding: '0.75rem 1.5rem',
  borderRadius: '0.5rem',
  border: 'none',
  color: colors.text.white,
  fontSize: '1rem',
  fontWeight: '600',
  fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem'
};

export default TempsList;
